/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 * Page News Create component
 *
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.25.1
 */

import { FC, SyntheticEvent, useCallback, useState } from 'react';

import { TabItem } from '@nexign/scc-ui-kit';
import { useDispatch, useSelector } from 'react-redux';

import { PageTitle } from '@/main/components/common';
import Title from '@/main/components/common/Title/Title';
import { appHistoryPush } from '@/main/modules/app/appHistory';
import PageLocale from '@/main/modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@/main/modules/internationalization/constants';
import { makeSelectPageNewsTranslation } from '@/main/modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@/main/modules/internationalization/types/common/pages';
import { modalOpen } from '@/main/modules/modal/modalActions';
import { RoutePath } from '@/main/routes/constants/routesConstants';

import { NewsCancelModal } from './components/NewsForms/NewsCancelModal';
import NewsExternalForm from './components/NewsForms/NewsExternalForm';
import NewsInternalForm from './components/NewsForms/NewsInternalForm';
import { ECreateNewsTypes } from './constants';
import { NewsFormContainerStyled, TabMenuStyled } from './styled/PageNewsCreateStyled';

const PageNewsCreate: FC = () => {
    const dispatch = useDispatch();

    const { createNews: translation } = useSelector(makeSelectPageNewsTranslation());

    const [currentTab, setCurrentTab] = useState<ECreateNewsTypes>(ECreateNewsTypes.INTERNAL);

    const handleTabChange = useCallback((ev: SyntheticEvent, value) => {
        ev.stopPropagation();
        setCurrentTab(value);
    }, []);

    const handleRedirectToNews = useCallback(() => {
        appHistoryPush(RoutePath.news);
    }, []);

    const handleCancelClick = useCallback(() => {
        dispatch(
            modalOpen({
                modalParams: {
                    isCustomCrossButton: true,
                },
                render: ({ onCloseModal }) => {
                    return <NewsCancelModal handleOk={handleRedirectToNews} onCloseModal={onCloseModal} />;
                },
            }),
        );
    }, [dispatch, handleRedirectToNews]);

    return (
        <div>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.pageNews]} />
            <Title pageName={ECommonTranslationPages.news} />
            <PageTitle title={translation.header.title} />
            <TabMenuStyled autoWidth value={currentTab} onChange={handleTabChange}>
                <TabItem label={translation.internalTab} value={ECreateNewsTypes.INTERNAL} />
                <TabItem label={translation.externalTab} value={ECreateNewsTypes.EXTERNAL} />
            </TabMenuStyled>
            <NewsFormContainerStyled>
                {currentTab === ECreateNewsTypes.EXTERNAL ? (
                    <NewsExternalForm handleCancel={handleCancelClick} handleRedirect={handleRedirectToNews} />
                ) : (
                    <NewsInternalForm handleCancel={handleCancelClick} handleRedirect={handleRedirectToNews} />
                )}
            </NewsFormContainerStyled>
        </div>
    );
};

export default PageNewsCreate;
